import { z } from 'zod';

export type Side = 'BUY' | 'SELL';
export type OrderType = 'Market' | 'Limit' | 'SL' | 'SL-M';
export type OrderStatus = 'PENDING' | 'TRIGGER' | 'EXECUTED' | 'CANCELLED' | 'REJECTED';

/**
 * Kite product types — how a position is carried, exactly as the real broker:
 *  - MIS  (intraday): leveraged, auto-squared-off at 15:20 IST same day.
 *  - CNC  (delivery): equity held overnight, no leverage, no square-off.
 *  - NRML (normal):   F&O positions carried to expiry.
 * A position's product decides whether the EOD square-off touches it.
 */
export type ProductType = 'MIS' | 'CNC' | 'NRML';
export const ProductTypeSchema = z.enum(['MIS', 'CNC', 'NRML']);

/** Result of submitting an order to the sim engine (screen 19). */
export type FillOutcome =
  /** `charges` is what the server actually debited for this leg, not an estimate. */
  | { kind: 'filled'; qty: number; avgPrice: number; charges: number }
  | { kind: 'partial'; filled: number; total: number }
  | { kind: 'rejected'; reason: string };

export const OrderSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  segment: z.string(), // "NSE · Equity"
  side: z.custom<Side>(),
  orderType: z.custom<OrderType>(),
  product: ProductTypeSchema.default('MIS'),
  status: z.custom<OrderStatus>(),
  qtyFilled: z.number(),
  qtyTotal: z.number(),
  /** The order's LIMIT price. Absent on Market/SL-M — they name no price. */
  price: z.number().optional(),
  /**
   * What it actually filled at, once any quantity has. This — not `price` —
   * is what an executed order is worth showing: a Market order has no price
   * until the book gives it one.
   */
  avgFillPrice: z.number().optional(),
  triggerPrice: z.number().optional(),
  ltp: z.number(),
  at: z.number(),
});
export type Order = z.infer<typeof OrderSchema>;

export const PositionSchema = z.object({
  symbol: z.string(),
  qty: z.number(),
  avgPrice: z.number(),
  ltp: z.number(),
  pnl: z.number(),
  product: ProductTypeSchema.default('MIS'),
  /** True when the sim engine closed this at the MIS square-off cutoff. */
  autoSquaredOff: z.boolean().default(false),
});
export type Position = z.infer<typeof PositionSchema>;

/**
 * A position the scope opened and flattened TODAY. It is gone from the book,
 * but its P&L is a real part of the day and the portfolio has to account for
 * it — otherwise closing a winner makes the day's number go down.
 */
export const ClosedPositionSchema = z.object({
  symbol: z.string(),
  product: ProductTypeSchema.default('MIS'),
  /** Quantity closed today. */
  qty: z.number(),
  avgPrice: z.number(),
  exitPrice: z.number(),
  realizedPnl: z.number(),
  /** When the closing fill happened. */
  at: z.number(),
});
export type ClosedPosition = z.infer<typeof ClosedPositionSchema>;

/** Local order-ticket state before submission (screen 08). */
export const OrderTicketSchema = z.object({
  symbol: z.string(),
  side: z.custom<Side>(),
  orderType: z.custom<OrderType>(),
  qty: z.number(),
  price: z.number().optional(),
});
export type OrderTicket = z.infer<typeof OrderTicketSchema>;

export const OptionChainRowSchema = z.object({
  strike: z.number(),
  callLtp: z.number(),
  putLtp: z.number(),
  atm: z.boolean().default(false),
});
export type OptionChainRow = z.infer<typeof OptionChainRowSchema>;

/**
 * Which fee schedule a trade falls under. Indian brokers price the four
 * segments very differently — an option pays flat brokerage and 0.1% STT on
 * the sell leg, an equity delivery trade pays no brokerage but STT on both —
 * so one blended percentage cannot stand in for all of them.
 */
export type ChargeSegment = 'equity_delivery' | 'equity_intraday' | 'future' | 'option';

/**
 * Derive the fee schedule from the symbol and the product it's carried under.
 * Options end in CE/PE (both the exchange's `NIFTY25JUL24000CE` and the
 * spaced `NIFTY 24000 CE` form the dev feeds use); futures end in FUT;
 * everything else is equity, delivery under CNC and intraday otherwise.
 */
export function chargeSegmentOf(symbol: string, product: ProductType): ChargeSegment {
  if (/(?:\d|\s)(CE|PE)$/.test(symbol)) return 'option';
  if (/FUT$/.test(symbol)) return 'future';
  return product === 'CNC' ? 'equity_delivery' : 'equity_intraday';
}

/**
 * Zerodha's published rate card, per segment. Rates are fractions of turnover
 * unless named otherwise. STT is asymmetric by design: it is the single
 * biggest line on a sell, and zero on the buy in every segment but delivery.
 */
const CHARGE_RATES: Record<
  ChargeSegment,
  {
    /** Flat rupees per order (options), or a percentage with a rupee cap. */
    brokerageFlat?: number;
    brokeragePct?: number;
    brokerageCap?: number;
    sttBuy: number;
    sttSell: number;
    /** Exchange transaction charge. */
    txn: number;
    /** Stamp duty — buy side only. */
    stampBuy: number;
  }
> = {
  equity_delivery: { brokeragePct: 0, sttBuy: 0.001, sttSell: 0.001, txn: 0.0000297, stampBuy: 0.00015 },
  equity_intraday: { brokeragePct: 0.0003, brokerageCap: 20, sttBuy: 0, sttSell: 0.00025, txn: 0.0000297, stampBuy: 0.00003 },
  future: { brokeragePct: 0.0003, brokerageCap: 20, sttBuy: 0, sttSell: 0.0002, txn: 0.0000173, stampBuy: 0.00002 },
  option: { brokerageFlat: 20, sttBuy: 0, sttSell: 0.001, txn: 0.0003503, stampBuy: 0.00003 },
};

/** SEBI turnover fee — the same tiny rate across every segment. */
const SEBI_RATE = 0.000001;
/** GST applies to brokerage and the regulator/exchange fees, never to STT or stamp. */
const GST_RATE = 0.18;

/**
 * What one executed leg costs, in rupees.
 *
 * The ticket's "Est. Charges" line and the server's actual debit are the same
 * function — a ticket that quotes a number nothing ever collects is a lie the
 * P&L eventually contradicts. The server is still the authority: it charges
 * what it computes from the price it filled at, not what the app displayed.
 *
 * Approximate by construction (a real contract note settles to the paisa and
 * varies by exchange), but the shape is right: options pay flat brokerage,
 * sells pay STT, buys pay stamp duty.
 */
export function estimateCharges(fill: {
  symbol: string;
  product: ProductType;
  side: Side;
  qty: number;
  price: number;
}): number {
  const turnover = Math.max(0, fill.qty * fill.price);
  if (!Number.isFinite(turnover) || turnover <= 0) return 0;
  const r = CHARGE_RATES[chargeSegmentOf(fill.symbol, fill.product)];
  const brokerage = r.brokerageFlat ?? Math.min(r.brokerageCap ?? Infinity, turnover * (r.brokeragePct ?? 0));
  const stt = turnover * (fill.side === 'BUY' ? r.sttBuy : r.sttSell);
  const txn = turnover * r.txn;
  const sebi = turnover * SEBI_RATE;
  const stamp = fill.side === 'BUY' ? turnover * r.stampBuy : 0;
  const gst = GST_RATE * (brokerage + txn + sebi);
  return Math.round((brokerage + stt + txn + sebi + stamp + gst) * 100) / 100;
}
